import React, { useState, useEffect, useMemo } from 'react';
import { Play, Plus } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../Components/UI/tooltip';
import { Input } from '../../../Components/UI/input';
import { Textarea } from '../../../Components/UI/textarea';
import { Label } from '../../../Components/UI/label';
import { SpeechControls } from '../../../Components/Common/SpeechControls';
import { currentUserHasPermission } from '../../../Utils/Utilities';
import { getConfigData } from '../../../Utils/Storage';
import OrderStagesDialog from '../../Settings/OrderStagesDialog';

const ThrDetailsStatus = ({
  threadData,
  threadTypeMeta,
  onStageUpdate,
  isOrder = false,
  connectionData,
  statusText: propStatusText,
  setStatusText: propSetStatusText,
  notes: propNotes,
  setNotes: propSetNotes,
  byEmail: propByEmail,
  setByEmail: propSetByEmail,
  byWhatsApp: propByWhatsApp,
  setByWhatsApp: propSetByWhatsApp,
}) => {
  const [internalStatusText, setInternalStatusText] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [internalByEmail, setInternalByEmail] = useState(false);
  const [internalByWhatsApp, setInternalByWhatsApp] = useState(false);

  const statusText = propStatusText !== undefined ? propStatusText : internalStatusText;
  const setStatusText = propSetStatusText || setInternalStatusText;
  const notes = propNotes !== undefined ? propNotes : internalNotes;
  const setNotes = propSetNotes || setInternalNotes;
  const byEmail = propByEmail !== undefined ? propByEmail : internalByEmail;
  const setByEmail = propSetByEmail || setInternalByEmail;
  const byWhatsApp = propByWhatsApp !== undefined ? propByWhatsApp : internalByWhatsApp;
  const setByWhatsApp = propSetByWhatsApp || setInternalByWhatsApp;

  // If uncontrolled (props not passed), prefill from StatusHist / notes
  useEffect(() => {
    if (propStatusText === undefined && propNotes === undefined) {
      try {
        const rawHist = threadData?.statusHist || threadData?.StatusHist;
        if (rawHist) {
          const hist = typeof rawHist === 'string' ? JSON.parse(rawHist) : rawHist;
          if (Array.isArray(hist) && hist.length > 0) {
            const last = hist[hist.length - 1];
            setInternalStatusText(last?.S || last?.Status || threadData?.currStage || threadData?.status || threadData?.Status || '');
            setInternalNotes(last?.N || last?.Notes || last?.D || threadData?.notes || '');
            return;
          }
        }
      } catch (e) {
        console.error('Error parsing StatusHist in ThrDetailsStatus:', e);
      }
      setInternalStatusText(threadData?.currStage || threadData?.status || threadData?.Status || '');
      setInternalNotes(threadData?.notes || '');
      setInternalByEmail(false);
      setInternalByWhatsApp(false);
    }
  }, [threadData, propStatusText, propNotes]);

  const [currentStageID, setCurrentStageID] = useState(threadData?.currStageID || 1);
  const [orderStagesDialogOpen, setOrderStagesDialogOpen] = useState(false);
  const [orderStagesInitialView, setOrderStagesInitialView] = useState('list');

  // Check permissions
  const canUpdateSts = currentUserHasPermission('ThrActions');
  const canMoveStages = canUpdateSts >= 1 && canUpdateSts <= 4;

  const configData = getConfigData();

  // Order stages reuse the Sale/Order thread type stages (ThreadStages TID 2)
  const orderStageOptions = useMemo(() => {
    if (!isOrder) return [];
    try {
      const parsed = JSON.parse(configData.ThreadStages || '[]');
      const stages = parsed.find(t => Number(t.TID) === 2)?.Stages || [];
      return stages.map(stage => ({
        id: stage.SID,
        name: stage.S
      })).filter(stage => stage.name);
    } catch (e) {
      return [];
    }
  }, [isOrder, configData.ThreadStages, orderStagesDialogOpen]);

  const { visibleOrderStages, otherOrderStages, selectedOtherStage, remainingOthersCount } = useMemo(() => {
    if (!orderStageOptions || orderStageOptions.length === 0) {
      return { visibleOrderStages: [], otherOrderStages: [], selectedOtherStage: null, remainingOthersCount: 0 };
    }

    const customStages = orderStageOptions.filter(s => {
      const lower = s.name.trim().toLowerCase();
      return lower !== 'pending' && lower !== 'completed';
    });

    const completedStage = { id: 'completed', name: 'Completed', label: 'Completed Sale' };

    const visibleCustom = customStages.slice(0, 4);
    const others = customStages.slice(4);

    // During sales edit: all other status chips (no Pending, no Completed Sale)
    const visible = [...visibleCustom];

    const isCurrentInVisible = visible.some(s => s.name.toLowerCase() === statusText.trim().toLowerCase());
    const matchedOther = !isCurrentInVisible && statusText.trim()
      ? others.find(s => s.name.toLowerCase() === statusText.trim().toLowerCase())
      : null;

    const remainingCount = matchedOther ? others.length - 1 : others.length;

    return {
      visibleOrderStages: visible,
      otherOrderStages: others,
      selectedOtherStage: matchedOther,
      remainingOthersCount: remainingCount,
    };
  }, [orderStageOptions, statusText]);

  // General Thread Stages for non-orders
  const threadStages = useMemo(() => {
    if (isOrder) return [];
    try {
      const parsed = JSON.parse(configData.ThreadStages || '[]');
      return parsed.find(t => t.TID === threadData?.typeID)?.Stages?.map(stage => ({
        id: stage.SID,
        name: stage.S
      })) || [];
    } catch (e) {
      return [];
    }
  }, [isOrder, configData.ThreadStages, threadData?.typeID]);

  // Update local state when threadData changes
  useEffect(() => {
    setCurrentStageID(threadData?.currStageID || 1);
  }, [threadData?.currStageID]);

  const stageColors = ['#062f12', '#094418', '#0b5d1e', '#107a28', '#17a53b'];

  const handleStageClick = (stageID) => {
    if (!canMoveStages) return;
    if (stageID !== currentStageID && onStageUpdate) {
      onStageUpdate(stageID, false);
    }
  };

  // If Order (TypeID === 2), render the new Category / Status Update section
  if (isOrder) {
    return (
      <div className="space-y-4">
        {/* Status Input & Chips */}
        <div className="space-y-1.5">
          <Label htmlFor="order-status-input" className="text-sm font-medium text-text-color">
            Status
          </Label>
          <Input
            id="order-status-input"
            value={statusText}
            onChange={(e) => setStatusText(e.target.value)}
            placeholder="e.g. Processing, In Production, Query..."
            className="bg-white text-sm h-10 px-3 py-2 leading-normal placeholder:text-sm placeholder:leading-normal placeholder:text-muted-foreground"
          />

          {/* Category-style Stage chips (from ThreadStages TID 2) */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {visibleOrderStages.map((stage) => {
              const isSelected = statusText.toLowerCase() === stage.name.toLowerCase();
              const displayLabel = stage.label || stage.name;
              return (
                <button
                  key={stage.id}
                  type="button"
                  onClick={() => setStatusText(isSelected ? '' : stage.name)}
                  className={`inline-flex items-center text-[13px] font-medium px-3 py-1.5 rounded-[5px] border transition-all ${isSelected
                    ? 'bg-white bg-sahi-primary-color/15 border-sahi-primary-color text-sahi-primary-color shadow-xs font-semibold'
                    : 'bg-white text-[#374151] border-gray-200 hover:bg-gray-50'
                    }`}
                >
                  <span className="max-w-[140px] truncate">{displayLabel}</span>
                </button>
              );
            })}

            {/* If selected stage is from other stages, show it as selected chip */}
            {selectedOtherStage && (
              <button
                type="button"
                onClick={() => setStatusText('')}
                className="inline-flex items-center text-[13px] font-medium px-3 py-1.5 rounded-[5px] border transition-all bg-sahi-primary-color/15 border-sahi-primary-color text-sahi-primary-color shadow-xs font-semibold"
              >
                <span className="max-w-[140px] truncate">{selectedOtherStage.name}</span>
              </button>
            )}

            {/* Others badge if there are more stages */}
            {remainingOthersCount > 0 && (
              <button
                type="button"
                onClick={() => {
                  setOrderStagesInitialView('list');
                  setOrderStagesDialogOpen(true);
                }}
                className="inline-flex items-center text-[13px] font-medium px-3 py-1.5 rounded-[5px] border transition-colors border-gray-200 bg-white text-sub-text-color hover:bg-gray-50 cursor-pointer"
              >
                +{remainingOthersCount} {remainingOthersCount === 1 ? 'other' : 'others'}
              </button>
            )}

            {/* Add / Manage Order Stages button */}
            <button
              type="button"
              onClick={() => {
                setOrderStagesInitialView('add');
                setOrderStagesDialogOpen(true);
              }}
              className="inline-flex items-center gap-1 text-[13px] font-medium px-3 py-1.5 rounded-[5px] border border-dashed border-gray-300 bg-white text-sub-text-color hover:border-sahi-primary-color hover:text-sahi-primary-color transition-colors cursor-pointer"
            >
              <Plus className="h-3.5 w-3.5" />
              New
            </button>
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-1.5">
          <Label htmlFor="order-status-notes" className="text-sm font-medium text-text-color">
            Notes
          </Label>
          <div className="relative w-full">
            <Textarea
              id="order-status-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add update notes or remarks..."
              rows={2}
              className="resize-none bg-white text-sm pr-20 py-2.5 leading-normal placeholder:text-sm placeholder:leading-normal placeholder:text-muted-foreground"
            />
            <SpeechControls
              targetId="order-status-notes"
              value={notes}
              onChange={(val) => setNotes(val)}
              isMobile={window.innerWidth < 1024}
              className="absolute right-2 top-2"
            />
          </div>
        </div>

        {/* Update Customer Options */}
        <div className="flex flex-wrap items-center gap-4 pt-1">
          <Label className="text-sm font-medium text-text-color">
            Update customer
          </Label>
          <div className="flex items-center gap-4">
            <label htmlFor="update-customer-email" className="inline-flex items-center gap-2 cursor-pointer text-sm font-medium text-text-color select-none">
              <input
                type="checkbox"
                id="update-customer-email"
                checked={byEmail}
                onChange={(e) => setByEmail(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-sahi-primary-color focus:ring-sahi-primary-color cursor-pointer accent-sahi-primary-color flex-shrink-0"
              />
              <span>Email</span>
            </label>

            <label htmlFor="update-customer-whatsapp" className="inline-flex items-center gap-2 cursor-pointer text-sm font-medium text-text-color select-none">
              <input
                type="checkbox"
                id="update-customer-whatsapp"
                checked={byWhatsApp}
                onChange={(e) => setByWhatsApp(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-sahi-primary-color focus:ring-sahi-primary-color cursor-pointer accent-sahi-primary-color flex-shrink-0"
              />
              <span>WhatsApp</span>
            </label>
          </div>
        </div>

        {/* Order Stages Configuration Dialog */}
        <OrderStagesDialog
          open={orderStagesDialogOpen}
          onClose={() => {
            setOrderStagesDialogOpen(false);
            setOrderStagesInitialView('list');
          }}
          initialView={orderStagesInitialView}
          selectedStage={statusText}
          onSelect={(stageName) => setStatusText(stageName)}
        />
      </div>
    );
  }

  // Default non-order stage pipeline (Inquiries, etc.)
  return (
    <div className="space-y-3 md:min-w-[500px] min-w-screen">
      <div className="bg-sahi-white rounded-lg p-4">
        <TooltipProvider>
          <div className="flex items-center gap-2 mb-8">
            {threadStages.map((stage, index) => (
              <Tooltip key={stage.id}>
                <TooltipTrigger asChild>
                  <div
                    className={`h-3 md:h-4 flex-1 rounded relative transition-all duration-200 ${!canMoveStages || !onStageUpdate
                      ? 'cursor-default'
                      : 'cursor-pointer hover:opacity-90'
                      }`}
                    style={{
                      backgroundColor: stageColors[index] || '#e5e7eb',
                    }}
                    onClick={() => handleStageClick(stage.id)}
                  >
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 flex flex-col items-center max-w-[90px] text-center">
                      {stage.id === currentStageID ? (
                        <Play className="h-3 w-3 md:h-4 md:w-4 fill-current -rotate-90 text-gray-600" />
                      ) : (
                        <Play className="h-3 w-3 md:h-4 md:w-4 -rotate-90 text-gray-600/30 md:opacity-100 opacity-0" />
                      )}

                      <span
                        className={`text-[10px] md:text-xs mt-1 font-semibold truncate whitespace-nowrap ${stage.id === currentStageID
                          ? 'text-gray-800'
                          : 'text-gray-800/30 md:opacity-100 opacity-0'
                          }`}
                      >
                        {stage.name.length > 12 ? (
                          <>
                            <span className="md:hidden">
                              {stage.name.slice(0, 6)}...
                            </span>
                            <span className="hidden md:inline">
                              {stage.name.slice(0, 9)}...
                            </span>
                          </>
                        ) : (
                          stage.name
                        )}
                      </span>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{stage.name}</p>
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        </TooltipProvider>
      </div>
    </div>
  );
};

export default ThrDetailsStatus;